import { Column } from '@access-erp/common';
import {
  CellClickedEvent,
  CellType,
  FilterValueTypes,
  ICellRendererParams,
  IRowNode,
  ValueFormatterParams,
} from '@access-erp/ui-components';

import { renderBillingType } from '@/components/common/EnquiriesScreen/customFilters/BillingTypeMultiSelect';
import { renderCustomerAccountCode }
  from '@/components/common/EnquiriesScreen/customFilters/CustomerAccountNumberMultiSelect';
import {
  createLinkCellRenderer,
  shouldSkipCellClickNavigation,
} from '@/components/common/EnquiriesScreen/components/LinkCellRenderer';
import { ContractFilterInput, ContractOutput } from '@/graphql/contracts/_generated_/contracts';
import { filtersforBillingTypes, filtersforStringifiedNumbers }
  from '@/components/common/EnquiriesScreen/utils/filtersForStringigfiedNumbers';
import { CellFormatters } from '@/components/common/EnquiriesScreen/utils/CellFormatters';
import { EnquiriesPill } from '@/components/common/EnquiriesScreen/components/EnquiriesPill';
import { PaginatedFilteredContracts } from '../hooks/useGetPaginatedFilteredContracts';
import { directDebitStatuses, filterStatuses, filterContractOrigins }
  from '@/components/common/EnquiriesScreen/utils/statuses.config';
import { getSubscriptionId } from '@/utils/auth/subscription.utils';
import { guidFilterOptions } from '@/pages/SalesOrders/columns/filters.config';
import { getParameterizedLink } from '@/utils/getLinkWithSubscriptionId.utils';

type DefaultColumnArgsType = {
  goToContract: (contractNumber: string, rowIndex: number, searchString?: string) => void;
  getRedirectLink: (path: string, id?: string, searchString?: string, rowIndex?: number) => string;
};

const getRowIndex = (e: CellClickedEvent) => {
  if (e.node.level === 0) {
    return e.rowIndex!;
  }
  return e.rowIndex! - e.node?.parent?.rowIndex! - 1;
};

const buildFilter: any = (currentFilter: ContractFilterInput, e: IRowNode<unknown>) => {
  if (e.level === 0) {
    return { ...currentFilter, [e.field!]: { eq: e.key } };
  }
  return buildFilter(
    e.group ? { ...currentFilter, [e.field!]: { eq: e.key } } : currentFilter,
    e.parent!,
  );
};

const setAppropriateFilter = async (
  e: IRowNode<unknown>,
): Promise<void> => {
  if (e.level === 0) {
    return;
  }
  const subscriptionId = await getSubscriptionId();
  let storageFilter = localStorage.getItem('currentContractsQuery:' + subscriptionId);
  const currentFilter = (storageFilter !== null) ? JSON.parse(storageFilter) : undefined;
  localStorage.setItem('currentContractsQuery:' + subscriptionId, JSON.stringify({
    ...(currentFilter ?? ({} as PaginatedFilteredContracts)),
    filter: buildFilter(currentFilter.filter, e),
  }));
};

const defaultColumns = ({
  goToContract,
  getRedirectLink,
}: DefaultColumnArgsType): Column<any>[] => {
  let filterIndex = 1;
  const nextFilter = () => filterIndex++;

  return [
    {
      name: 'contractNumber',
      caption: 'Contract Number',
      width: 200,
      cellType: CellType.String,
      cellRenderer: createLinkCellRenderer<ContractOutput>(({ data, rowIndex, searchString }) => {
        const contractId = data?.id;
        if (!contractId) {
          return '#';
        }
        const link = getRedirectLink(window.location.pathname, contractId, searchString, rowIndex);
        return getParameterizedLink(link);
      }),
      onCellClicked: async (e: CellClickedEvent) => {
        if (shouldSkipCellClickNavigation(e)) {
          return;
        }
        const searchStringIfAny = e.context?.serverSide?.current?.search;
        await setAppropriateFilter(e.node);
        goToContract(e.data.id, getRowIndex(e), searchStringIfAny);
      },
      defaultFilter: nextFilter(),
    },

    {
      name: 'customerAccountNumber',
      caption: 'Contracted Account',
      width: 200,
      cellType: CellType.String,
      filterType: FilterValueTypes.StringMultiSelectFilter,
      renderFilter: (renderProps: any) =>
        renderCustomerAccountCode({ ...renderProps, title: 'Account Number' }),
      defaultFilter: nextFilter(),
    },
    {
      name: 'customerName',
      caption: 'Contracted Name',
      width: 200,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'contractStartDate',
      caption: 'Contract Start Date',
      width: 150,
      cellType: CellType.Date,
      cellFormatter: CellFormatters.date,
      defaultFilter: nextFilter(),
    },
    {
      name: 'contractEndDate',
      caption: 'Contract End Date',
      width: 150,
      cellType: CellType.Date,
      cellFormatter: CellFormatters.date,
      defaultFilter: nextFilter(),
    },
    {
      name: 'term',
      caption: 'Contract Term',
      width: 130,
      cellType: CellType.Integer,
      cellFormatter: CellFormatters.integer,
      defaultFilter: nextFilter(),
    },
    {
      name: 'renewableTerm',
      caption: 'Renewable Term',
      width: 130,
      cellType: CellType.Integer,
      cellFormatter: CellFormatters.integer,
      defaultFilter: nextFilter(),
    },
    {
      name: 'totalContractValue',
      caption: 'Total Value',
      width: 200,
      cellType: CellType.String,
      cellFormatter: (params: ValueFormatterParams) =>
        CellFormatters.currency(params, params.data.currency),
      defaultFilter: nextFilter(),
      filterType: FilterValueTypes.StringFilter,
      filterOptions: {
        availableOptions: filtersforStringifiedNumbers,
      },
      cellStyle: { justifyContent: 'end' },
    },
    {
      name: 'totalBillingPeriodValue',
      caption: 'Total Billing Period Value',
      width: 200,
      cellType: CellType.String,
      cellFormatter: (params: ValueFormatterParams) =>
        CellFormatters.currency(params, params.data.currency),
      defaultFilter: nextFilter(),
      filterType: FilterValueTypes.StringFilter,
      filterOptions: {
        availableOptions: filtersforStringifiedNumbers,
      },
      cellStyle: { justifyContent: 'end' },
    },
    {
      name: 'clientDatabaseReference',
      caption: 'Database',
      width: 200,
      cellType: CellType.String,
      sortable: false,
      filterable: false,
      disableRowGroup: true,
      suppressAdvancedFilter: true,
      defaultFilter: nextFilter(),
    },
    {
      name: 'contractStatus',
      caption: 'Contract Status',
      width: 170,
      cellType: CellType.String,
      filterType: FilterValueTypes.EnumFilter,
      filterData: filterStatuses,
      defaultFilter: nextFilter(),
    },
    {
      name: 'directDebit',
      caption: 'Direct Debit',
      width: 170,
      cellType: CellType.String,
      filterType: FilterValueTypes.EnumFilter,
      filterData: directDebitStatuses,
      defaultFilter: nextFilter(),
    },
    {
      name: 'consolidateBilling',
      caption: 'Consolidated',
      width: 150,
      cellType: CellType.Bool,
      defaultFilter: nextFilter(),
    },
    {
      name: 'productFamily',
      caption: 'Product Family',
      width: 280,
      cellType: CellType.String,
      cellRenderer: (params: ICellRendererParams) => {
        const productFamilyArray = params.data?.productFamily?.split(',');
        return <EnquiriesPill value={productFamilyArray ?? ''} mainProduct={params.data.mainProductFamily} />;
      },
      defaultFilter: nextFilter(),
    },
    {
      name: 'billingType',
      caption: 'Billing Type',
      width: 280,
      cellType: CellType.String,
      filterType: FilterValueTypes.StringMultiSelectFilter,
      renderFilter: (renderProps: any) => renderBillingType({ ...renderProps, title: 'Billing Type' }),
      filterOptions: {
        availableOptions: filtersforBillingTypes,
      },
      cellRenderer: (params: ICellRendererParams) => {
        const billingTypesArray = params.data?.billingType?.split?.(',');
        return <EnquiriesPill value={billingTypesArray ?? ''} />;
      },
      defaultFilter: nextFilter(),
    },
    {
      name: 'externalContractId',
      caption: 'External Contract ID',
      width: 160,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'rolloverDate',
      caption: 'Rollover Date',
      width: 150,
      cellType: CellType.Date,
      cellFormatter: CellFormatters.date,
      defaultFilter: nextFilter(),
    },
    {
      name: 'calculatedTerm',
      caption: 'Calculated Term',
      width: 200,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'address',
      caption: 'Contracted Address',
      width: 300,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'postCode',
      caption: 'Contracted Postcode',
      width: 160,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'origin',
      caption: 'Origin',
      width: 130,
      cellType: CellType.String,
      filterType: FilterValueTypes.EnumFilter,
      filterData: filterContractOrigins,
      defaultFilter: nextFilter(),
    },
    {
      name: 'oneEvoMigrated',
      caption: 'OneEvo Migrated',
      width: 150,
      cellType: CellType.Bool,
      defaultFilter: nextFilter(),
    },
    {
      name: 'oneEvoContractNumber',
      caption: 'OneEvo Contract Number',
      width: 200,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
    },
    {
      name: 'oneEvoMigrationDate',
      caption: 'OneEvo Migration Date',
      width: 170,
      cellType: CellType.Date,
      cellFormatter: CellFormatters.date,
      defaultFilter: nextFilter(),
    },
    {
      name: 'id',
      caption: 'ID',
      width: 270,
      cellType: CellType.String,
      defaultFilter: nextFilter(),
      hidden: true,
      filterType: FilterValueTypes.StringFilter,
      filterOptions: {
        availableOptions: guidFilterOptions,
      }
    },
  ];
};

export const getHeaderColumns = (defaultColumnArgs: DefaultColumnArgsType): Column<any>[] =>
  defaultColumns(defaultColumnArgs);
